import { useState, useEffect } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import {
  LayoutDashboard, Users, Store, ShoppingBag, Truck, Tags, Ticket, Gift,
  MessageSquare, MapPin, Settings, ShieldCheck, ScrollText, Menu, X, Sun, Moon,
  LogOut, Package, Percent, FileText, Wallet, Archive, Banknote,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/lib/ui";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const NAV = [
  { section: "Overview", items: [{ to: "/", icon: LayoutDashboard, label: "Dashboard", testid: "dashboard", end: true }] },
  { section: "Operations", items: [
    { to: "/shops", icon: Store, label: "Shops", testid: "shops", badge: "shops" },
    { to: "/orders", icon: ShoppingBag, label: "Orders", testid: "orders" },
    { to: "/delivery", icon: Truck, label: "Delivery", testid: "delivery" },
    { to: "/users", icon: Users, label: "Users", testid: "users" },
  ]},
  { section: "Catalog", items: [
    { to: "/catalog", icon: Package, label: "Categories & Products", testid: "catalog" },
  ]},
  { section: "Marketing", items: [
    { to: "/coupons", icon: Ticket, label: "Coupons", testid: "coupons" },
    { to: "/referrals", icon: Gift, label: "Referrals & Loyalty", testid: "referrals" },
  ]},
  { section: "Moderation", items: [
    { to: "/moderation", icon: MessageSquare, label: "Reviews & Support", testid: "moderation", badge: "tickets" },
  ]},
  { section: "System", items: [
    { to: "/service-areas", icon: MapPin, label: "Service Areas", testid: "service-areas" },
    { to: "/settings", icon: Settings, label: "Settings", testid: "settings" },
    { to: "/commission", icon: Percent, label: "Commission", testid: "commission" },
    { to: "/earnings", icon: Wallet, label: "Shop Earnings", testid: "earnings" },
    { to: "/payouts", icon: Banknote, label: "Payouts & Settings", testid: "payouts" },
    { to: "/compliance", icon: FileText, label: "Policies, Tax & Refunds", testid: "compliance" },
    { to: "/roles", icon: ShieldCheck, label: "Roles & Permissions", testid: "roles" },
    { to: "/logs", icon: ScrollText, label: "Security Logs", testid: "logs" },
    { to: "/data", icon: Archive, label: "Data Management", testid: "data" },
  ]},
];

function useDarkMode() {
  const [dark, setDark] = useState(() => localStorage.getItem("sonu_theme") === "dark");
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("sonu_theme", dark ? "dark" : "light");
  }, [dark]);
  return [dark, setDark];
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useDarkMode();
  const nav = useNavigate();
  const { data: stats } = useFetch("/admin/dashboard", []);
  const badges = { shops: stats?.shops_pending || 0, tickets: stats?.open_tickets || 0 };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-[260px] bg-card border-r flex flex-col transition-transform lg:translate-x-0",
          open ? "translate-x-0" : "-translate-x-full"
        )}
        data-testid="admin-sidebar"
      >
        <div className="h-16 flex items-center gap-2 px-5 border-b shrink-0">
          <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center text-primary-foreground font-extrabold font-outfit">S</div>
          <div className="leading-tight">
            <div className="font-extrabold font-outfit text-[15px]">Sonu Ki Dukaan</div>
            <div className="text-[10px] text-muted-foreground font-mono">Bachat Ki Dukaan</div>
          </div>
          <button className="ml-auto lg:hidden" onClick={() => setOpen(false)} data-testid="sidebar-close-btn"><X className="h-5 w-5" /></button>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
          {NAV.map((grp) => (
            <div key={grp.section}>
              <div className="px-2 mb-1.5 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{grp.section}</div>
              <div className="space-y-0.5">
                {grp.items.map((it) => (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    end={it.end}
                    onClick={() => setOpen(false)}
                    data-testid={`nav-${it.testid}`}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                        isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )
                    }
                  >
                    <it.icon className="h-4 w-4 shrink-0" />
                    <span className="flex-1">{it.label}</span>
                    {it.badge && badges[it.badge] > 0 && (
                      <span className="text-[10px] font-bold bg-rose-500 text-white rounded-full px-1.5 py-0.5 tnum">{badges[it.badge]}</span>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </aside>

      {open && <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="lg:pl-[260px]">
        <header className="sticky top-0 z-30 h-16 bg-card/80 backdrop-blur border-b flex items-center gap-3 px-4 sm:px-6">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="sidebar-open-btn"><Menu className="h-5 w-5" /></button>
          <span className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
            <MapPin className="h-3 w-3" /> Lucknow · Live
          </span>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => setDark(!dark)} data-testid="theme-toggle-btn">
              {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <div className="hidden sm:flex items-center gap-2 pl-2 border-l">
              <div className="h-8 w-8 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-bold">
                {(user?.name || "A").slice(0, 1).toUpperCase()}
              </div>
              <div className="leading-tight">
                <div className="text-xs font-semibold" data-testid="admin-name">{user?.name || "Admin"}</div>
                <div className="text-[10px] text-muted-foreground">{user?.email || user?.phone}</div>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={logout} data-testid="logout-btn">
              <LogOut className="h-3.5 w-3.5 sm:mr-2" /> <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1600px]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
